import {
  feedScrape,
  type FeedScrape,
  type ParsedList,
  type Result,
  type VideoSummary,
} from '@visious/core';
import { describeProblem, type ProblemCopy } from '../../api/problem';
import type { ApiProblem } from '../../api/videos';
import {
  matchesGridSearch,
  toListingItems,
  type ListingItem,
  type ProgressLookup,
} from '../../listing/model';
import { text } from '../../strings';

/**
 * §8's Home/Popular row, as reasoning — R13: no DOM, no React, no `RefObject`. `useFeed.ts`
 * feeds it and `Feed.tsx` renders what it returns.
 *
 * Two endpoints share this because they are one page in §8's table and one shape on the wire. What
 * differs between them is four facts, which is what {@link FEED_SPECS} holds, and only one of the
 * four is cosmetic.
 */

export type FeedKind = 'popular' | 'trending';

export interface FeedSpec {
  readonly heading: string;
  /** What the list actually is, where its name does not say. */
  readonly lede: string;
  readonly emptyTitle: string;
  readonly emptyDetail: string;
  /**
   * Whether a row may carry an absolute-date tooltip. `/popular`'s `published` is genuine — 40 rows
   * with 39 distinct times of day — while trending's is back-computed from the relative text like
   * search's, so an exact date there would be an invention (API reference §2).
   */
  readonly absoluteDates: boolean;
  /** Sized to the widest grid this page can produce, so the skeletons fill the fold rather than a strip. */
  readonly skeletonCards: number;
}

export function feedSpecs(): Readonly<Record<FeedKind, FeedSpec>> {
  const words = text().feed;
  return {
    popular: {
      heading: words.popular.heading,
      lede: words.popular.lede,
      emptyTitle: words.popular.emptyTitle,
      emptyDetail: words.popular.emptyDetail,
      absoluteDates: true,
      skeletonCards: 12,
    },
    trending: {
      heading: words.trending.heading,
      lede: words.trending.lede,
      emptyTitle: words.trending.emptyTitle,
      emptyDetail: words.trending.emptyDetail,
      absoluteDates: false,
      skeletonCards: 12,
    },
  };
}

/*
 * 2b had two notices here and neither survived a read of what they said: "the instance returned 40 rows
 * carrying titles only" and "3 rows could not be read and are not shown". Both are statements about the
 * response rather than about the videos on the page, and a reader can act on neither. The measurements
 * they were drawn from are still made — `scrape` is below, and the fold still counts its drops.
 */

export type FeedState =
  | { readonly kind: 'loading'; readonly skeletonCards: number }
  /**
   * §8's "grey out with a reason". It used to carry an `alternative` — the way to the *other* feed page
   * — which the retired Trending screen took with it: there is no second feed page to send anyone
   * to, and a link from the popular page to the trending section directly above it would be a link to
   * where the reader already is. What is left is the panel's own Retry.
   */
  | { readonly kind: 'problem'; readonly copy: ProblemCopy }
  | { readonly kind: 'empty'; readonly title: string; readonly detail: string }
  | {
      readonly kind: 'ready';
      readonly items: readonly ListingItem[];
      /** The grid search emptied the page — a sentence, or an empty grid reads as a dead feed. */
      readonly nothingMatches: boolean;
      /** Exposed for the driven-browser checks: what the rows were missing, measured rather than said. */
      readonly scrape: FeedScrape;
    };

export interface FeedStateOptions {
  readonly nowEpochSec: number;
  /** 4b's stored playheads. Local, so Home and Trending keep making no request while signed out. */
  readonly progress: ProgressLookup;
  /** The grid search — display-only over the one response these feeds arrive as. */
  readonly search: string;
}

/**
 * 320, and it is not a compromise between two sensible numbers — it is the only one available.
 *
 * `videoThumbnails[]` declares nine sizes and exactly **three** of them are 16:9: `maxres` and
 * `maxresdefault` at 1280×720, and `medium` at 320×180. There is nothing in between, so a grid card
 * ~340 px wide can be fed a 320 px source (a 6 % upscale, `mqdefault.jpg`, **9–15 KB**) or a 1280 px
 * one (`maxres.jpg`, **102 KB**) — measured on this instance, both served. At 40 cards that is 360 KB
 * against 4 MB for a page of thumbnails nobody looks at closely, so asking for 480 "to be safe on a
 * retina display" buys the largest image the API has, eleven times over.
 *
 * The 4:3 sizes are not a way out either: `high`/`hqdefault` is 480×360 at 13 KB and would crop
 * cleanly under `object-fit: cover` *if* YouTube always letterboxes a 16:9 frame into that canvas —
 * but 1f measured a video where even `maxres` comes back 4:3, so the assumption is exactly the kind
 * that breaks on one row in fifty. The rail already asks for 320 and 1f graded it sharp at 168 px.
 */
const CARD_THUMBNAIL_MIN_WIDTH = 320;

export function feedStateFor(
  kind: FeedKind,
  result: Result<ParsedList<VideoSummary>, ApiProblem> | null,
  options: FeedStateOptions,
): FeedState {
  const spec = feedSpecs()[kind];
  if (result === null) return { kind: 'loading', skeletonCards: spec.skeletonCards };

  if (!result.ok) {
    return { kind: 'problem', copy: describeProblem(result.error, problemSubjectFor(kind)) };
  }

  const { items: videos, dropped } = result.value;
  if (videos.length === 0) {
    // An empty list with drops is *not* an empty feed — it is a feed we could not read. Saying
    // "nothing popular yet" about a page whose every row failed to parse would be the R4 count dying
    // silently, one layer above the parser this time.
    if (dropped.length > 0) {
      return {
        kind: 'problem',
        copy: {
          title: text().feed[kind].unreadableTitle,
          detail: text().feed.allDropped,
          retryable: true,
        },
      };
    }
    return { kind: 'empty', title: spec.emptyTitle, detail: spec.emptyDetail };
  }

  const scrape = feedScrape(videos, options.nowEpochSec);

  const all = toListingItems(videos, {
    nowEpochSec: options.nowEpochSec,
    thumbnailMinWidth: CARD_THUMBNAIL_MIN_WIDTH,
    absoluteDates: spec.absoluteDates,
    scrape,
    progress: options.progress,
  });
  const items = all.filter((item) => matchesGridSearch(options.search, item.title, item.author));

  return {
    kind: 'ready',
    items,
    nothingMatches: all.length > 0 && items.length === 0,
    scrape,
  };
}

function problemSubjectFor(kind: FeedKind) {
  const words = text().feed.problem;
  const noun = text().feed[kind].noun;
  return {
    notFound: {
      title: words.notFoundTitle(noun),
      detail: words.notFoundDetail,
      retryable: false,
    },
    unreadable: (): ProblemCopy => ({ ...words.unreadable, title: words.unreadableTitle(noun) }),
    // §8: "grey out Popular **with a reason** if the instance disabled it". `popular_enabled: false`
    // is a real Invidious setting and it answers **403** — the same status as an expired media URL,
    // which is why core needs `endpoint-disabled` for this sentence to be reachable at all.
    disabled: {
      title: words.disabledTitle(noun),
      detail: words.disabledDetail,
      retryable: false,
    },
  };
}
